import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Edit, Eye, Plus, Search, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { createCustomer, deleteCustomer, listCustomers, updateCustomer } from '../api/customers';
import { getErrorMessage } from '../api/response';
import Button from '../components/Button';
import ConfirmDialog from '../components/ConfirmDialog';
import FormField, { inputClassName } from '../components/FormField';
import Modal from '../components/Modal';
import PageHeader from '../components/PageHeader';
import Table from '../components/Table';
import { LoadingState } from '../components/PageState';
import useDebouncedValue from '../hooks/useDebouncedValue';
import { date } from '../utils/format';


const emptyForm = {
  full_name: '',
  email: '',
  phone: '',
  address: ''
};

const Clientes = () => {
  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [confirm, setConfirm] = useState(null);

  const limit = 25;
  const debouncedSearch = useDebouncedValue(search.trim());

  const loadCustomers = useCallback(async (overrides = {}) => {
    try {
      setLoading(true);
      const data = await listCustomers({
        search: overrides.search ?? debouncedSearch,
        page: overrides.page ?? page,
        limit
      });
      setCustomers(data);
    } catch (error) {
      toast.error(getErrorMessage(error, 'No se pudieron cargar los clientes'));
    } finally {
      setLoading(false);
    }
  }, [page, debouncedSearch, limit]);

  useEffect(() => {
    loadCustomers();
  }, [loadCustomers]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setModalOpen(true);
  };

  const openEdit = (customer) => {
    setEditing(customer);
    setForm({
      full_name: customer.full_name || '',
      email: customer.email || '',
      phone: customer.phone || '',
      address: customer.address || ''
    });
    setModalOpen(true);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    try {
      setSaving(true);
      if (editing) {
        await updateCustomer(editing.id, form);
        toast.success('Cliente actualizado');
      } else {
        await createCustomer(form);
        toast.success('Cliente creado');
      }
      setModalOpen(false);
      loadCustomers();
    } catch (error) {
      toast.error(getErrorMessage(error, 'No se pudo guardar el cliente'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm) return;

    try {
      setSaving(true);
      await deleteCustomer(confirm.id);
      toast.success('Cliente eliminado');
      setConfirm(null);
      loadCustomers();
    } catch (error) {
      toast.error(getErrorMessage(error, 'No se pudo eliminar el cliente'));
    } finally {
      setSaving(false);
    }
  };

  const submitSearch = (event) => {
    event.preventDefault();
    setPage(1);
    loadCustomers({ page: 1, search: search.trim() });
  };

  const handleSearchChange = (event) => {
    setSearch(event.target.value);
    setPage(1);
  };

  const columns = [
    {
      key: 'full_name',
      label: 'Cliente',
      render: (row) => (
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full bg-gray-100 text-sm font-bold text-gray-500">
            {row.photo_url ? (
              <img src={row.photo_url} alt={row.full_name} className="h-full w-full object-cover" />
            ) : (
              row.full_name?.slice(0, 1) || '#'
            )}
          </div>
          <div>
            <p className="font-semibold text-gray-900">{row.full_name}</p>
            <p className="text-xs text-gray-500 font-mono">{row.uuid || row.id}</p>
          </div>
        </div>
      )
    },
    { key: 'email', label: 'Email', render: (row) => row.email || '-' },
    { key: 'phone', label: 'Teléfono', render: (row) => row.phone || '-' },
    { key: 'created_at', label: 'Registro', render: (row) => date(row.created_at) },
    {
      key: 'actions',
      label: 'Acciones',
      render: (row) => (
        <div className="flex items-center gap-2">
          <Link to={`/clientes/${row.uuid || row.id}`} className="rounded-md p-2 text-gray-600 hover:bg-gray-100" title="Ver ficha">
            <Eye className="h-4 w-4" />
          </Link>
          <button onClick={() => openEdit(row)} className="rounded-md p-2 text-blue-700 hover:bg-blue-50" title="Editar">
            <Edit className="h-4 w-4" />
          </button>
          <button onClick={() => setConfirm(row)} className="rounded-md p-2 text-red-700 hover:bg-red-50" title="Eliminar">
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      )
    }
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Clientes"
        description="Ficha comercial y operacional de tutores: contacto, mascotas, citas, compras y pagos."
        actions={<Button icon={Plus} onClick={openCreate}>Nuevo cliente</Button>}
      />

      <section className="rounded-lg border border-gray-200 bg-white p-4">
        <form onSubmit={submitSearch} className="flex flex-col gap-3 md:flex-row">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            <input
              value={search}
              onChange={handleSearchChange}
              className={`${inputClassName} pl-9`}
              placeholder="Buscar por nombre, telefono, email o documento"
            />
          </div>
          <Button type="submit" variant="secondary">Buscar</Button>
        </form>
      </section>

      <section className="rounded-lg border border-gray-200 bg-white">
        {loading ? <div className="p-5"><LoadingState rows={5} /></div> : (
          <Table columns={columns} data={customers} emptyMessage="No hay clientes para los filtros seleccionados" />
        )}
        <div className="flex items-center justify-between border-t border-gray-200 px-4 py-3">
          <Button variant="secondary" disabled={page === 1 || loading} onClick={() => setPage((value) => value - 1)}>
            Anterior
          </Button>
          <span className="text-sm text-gray-600">Pagina {page}</span>
          <Button variant="secondary" disabled={customers.length < limit || loading} onClick={() => setPage((value) => value + 1)}>
            Siguiente
          </Button>
        </div>
      </section>

      <Modal
        open={modalOpen}
        title={editing ? 'Editar cliente' : 'Nuevo cliente'}
        onClose={() => setModalOpen(false)}
        footer={
          <div className="flex justify-end gap-2">
            <Button type="button" variant="secondary" onClick={() => setModalOpen(false)} disabled={saving}>Cancelar</Button>
            <Button type="submit" form="customer-form" disabled={saving}>{saving ? 'Guardando...' : 'Guardar'}</Button>
          </div>
        }
      >
        <form id="customer-form" onSubmit={handleSubmit} className="grid gap-4 md:grid-cols-2">
          <FormField label="Nombre completo" required>
            <input className={inputClassName} value={form.full_name} onChange={(event) => setForm({ ...form, full_name: event.target.value })} required />
          </FormField>
          <FormField label="Email">
            <input type="email" className={inputClassName} value={form.email} onChange={(event) => setForm({ ...form, email: event.target.value })} />
          </FormField>
          <FormField label="Telefono">
            <input className={inputClassName} value={form.phone} onChange={(event) => setForm({ ...form, phone: event.target.value })} />
          </FormField>
          <FormField label="Direccion">
            <input className={inputClassName} value={form.address} onChange={(event) => setForm({ ...form, address: event.target.value })} />
          </FormField>
        </form>
      </Modal>

      <ConfirmDialog
        open={Boolean(confirm)}
        title="Eliminar cliente"
        message={`Se eliminara el cliente ${confirm?.full_name}. Esta accion no se puede deshacer.`}
        confirmLabel="Eliminar"
        loading={saving}
        onCancel={() => setConfirm(null)}
        onConfirm={handleDelete}
      />
    </div>
  );
};

export default Clientes;
